// ─────────────────────────────────────────────────────────────────────────────
// InstrumentLandingPage.jsx — Reusable landing page template driven by
// content configs in instrumentPages.js / solutionPages.js.
// Renders one page per instrument/solution at /instruments/:slug and /solutions/:slug
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";
import { INSTRUMENT_PAGES } from "../constants/instrumentPages.js";
import { SOLUTION_PAGES } from "../constants/solutionPages.js";
import { seoDescription, pageTitle } from "../utils/seoText.js";
import { relatedForInstrument, SOLUTION_SLUGS, FEATURED_INSTRUMENTS } from "../constants/relatedPages.js";

const N="#0a1628",W="#ffffff",CR="#f7f5f1",B="#e2ddd6",MU="#7a8398",SL="#475569";
const GOLD="#c8964e",BLUE="#1d4ed8";
const PHONE_D = "+91 76767 09796";
const WA_URL = `https://wa.me/917676709796?text=Hi%2C%20I%27m%20interested%20in%20AXIS360%20Assess.%20Can%20you%20share%20more%20details%3F`;
const DOMAIN = "https://assess.nonverbals.com";

export default function InstrumentLandingPage({ slug, category, onBack, onEnter }){
  const registry = category === "solution" ? SOLUTION_PAGES : INSTRUMENT_PAGES;
  const page = registry[slug];

  useEffect(() => {
    if (!page) {
      // The server returns 200 for any URL, so an unknown slug would look like a real page: tell crawlers not to index it.
      const robots = document.createElement("meta");
      robots.name = "robots"; robots.content = "noindex";
      document.head.appendChild(robots);
      return () => { try { document.head.removeChild(robots); } catch (e) { /* already gone */ } };
    }
    const prevTitle = document.title;
    document.title = pageTitle(page.name);
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name","description"); document.head.appendChild(meta); }
    meta.setAttribute("content", seoDescription(page.tagline, page.hero));

    const scriptId = "instrument-page-schema";
    let script = document.getElementById(scriptId);
    if (!script) { script = document.createElement("script"); script.id = scriptId; script.type = "application/ld+json"; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      "@context":"https://schema.org",
      "@type": category === "solution" ? "Service" : "Product",
      "name": page.name,
      "description": page.hero,
      "provider": { "@type":"Organization", "name":"NonVerbals Consulting", "url":"https://nonverbals.com" },
      "url": `${DOMAIN}/${category === "solution" ? "solutions" : "instruments"}/${slug}`,
    });

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, [page, slug, category]);

  if (!page) {
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",
        fontFamily:"'Plus Jakarta Sans',sans-serif",background:CR}}>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:20,fontWeight:700,color:N,marginBottom:10}}>Page not found</div>
          <button onClick={onBack} style={{background:N,color:W,border:"none",borderRadius:10,
            padding:"10px 22px",fontSize:13,fontWeight:700,cursor:"pointer"}}>← Back to Home</button>
        </div>
      </div>
    );
  }

  const isInstrument = category !== "solution";
  const kicker = isInstrument ? "AXIS360 Instrument" : "AXIS360 Solution";
  const backLabel = isInstrument ? "All Instruments" : "All Solutions";

  return (
    <div style={{fontFamily:"'Plus Jakarta Sans',sans-serif",background:W,color:N,minHeight:"100vh",
      overflowX:"hidden",width:"100%",boxSizing:"border-box"}}>
      <style>{`
        * { box-sizing: border-box; }
        @media (max-width: 640px) {
          .ilp-nav-back-label { display:none; }
          .ilp-hero-pad { padding-top:36px !important; padding-bottom:28px !important; }
          .ilp-section-pad { padding-bottom:32px !important; }
          .ilp-cta-btn { width:100%; text-align:center; }
          .ilp-final-cta-btn { width:100%; text-align:center; }
        }
      `}</style>

      <div style={{position:"sticky",top:0,zIndex:50,background:W,borderBottom:`1px solid ${B}`,padding:"12px 16px"}}>
        <div style={{maxWidth:1100,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
          <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",
            display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:N,fontFamily:"inherit",flexShrink:0}}>
            <span style={{fontFamily:"Fraunces,serif",fontSize:18}}>AXIS<span style={{color:GOLD}}>360</span></span>
          </button>
          <div style={{display:"flex",gap:12,alignItems:"center",minWidth:0}}>
            <button onClick={onBack} className="ilp-nav-back-label" style={{background:"none",border:"none",cursor:"pointer",
              fontSize:12,color:SL,fontFamily:"inherit",whiteSpace:"nowrap"}}>← {backLabel}</button>
            <button onClick={onEnter} style={{background:N,color:W,border:"none",borderRadius:9,
              padding:"9px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>
              Get Started
            </button>
          </div>
        </div>
      </div>

      <div className="ilp-hero-pad" style={{background:`linear-gradient(180deg,${page.color}08 0%,${W} 60%)`,padding:"56px 20px 40px"}}>
        <div style={{maxWidth:900,margin:"0 auto"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
            <span style={{fontSize:26}}>{page.icon}</span>
            <span style={{fontSize:9,fontWeight:800,color:page.color,letterSpacing:2.5,
              textTransform:"uppercase",background:page.color+"14",padding:"4px 12px",borderRadius:99}}>
              {kicker}
            </span>
          </div>
          <h1 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(28px,6vw,42px)",fontWeight:400,lineHeight:1.15,
            letterSpacing:-0.5,marginBottom:16,color:N,wordBreak:"break-word"}}>{page.name}</h1>
          <p style={{fontSize:"clamp(14px,3vw,16px)",color:SL,marginBottom:20,fontWeight:500}}>{page.tagline}</p>
          <p style={{fontSize:"clamp(13px,2.5vw,14px)",color:SL,lineHeight:1.75,maxWidth:760,marginBottom:28}}>{page.hero}</p>
          <div className="ilp-cta-row" style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <button onClick={onEnter} className="ilp-cta-btn" style={{background:N,color:W,border:"none",borderRadius:10,
              padding:"13px 26px",fontSize:13.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              Start Free Trial →
            </button>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="ilp-cta-btn" style={{background:W,color:N,
              border:`1.5px solid ${B}`,borderRadius:10,padding:"13px 26px",fontSize:13.5,fontWeight:700,
              cursor:"pointer",fontFamily:"inherit",textDecoration:"none",display:"inline-block"}}>
              💬 Talk to Us — {PHONE_D}
            </a>
          </div>
        </div>
      </div>

      <div className="ilp-section-pad" style={{maxWidth:900,margin:"0 auto",padding:"0 20px 40px"}}>
        <div style={{display:"grid",gridTemplateColumns:`repeat(auto-fit,minmax(130px,1fr))`,gap:12}}>
          {page.stats.map((s,i)=>(
            <div key={i} style={{background:CR,borderRadius:12,padding:"18px 14px",textAlign:"center",
              border:`1px solid ${B}`,borderTop:`3px solid ${page.color}`}}>
              <div style={{fontFamily:"Fraunces,serif",fontSize:24,color:page.color,fontWeight:400,marginBottom:4}}>{s.v}</div>
              <div style={{fontSize:9.5,color:MU,fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ WHAT ═══ */}
      <div className="ilp-section-pad" style={{maxWidth:900,margin:"0 auto",padding:"0 20px 48px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <span style={{fontSize:9,fontWeight:800,color:page.color,letterSpacing:2.5,textTransform:"uppercase",
            background:page.color+"14",padding:"4px 12px",borderRadius:99}}>What</span>
          <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(20px,4vw,26px)",fontWeight:400,color:N,margin:0}}>
            What's included
          </h2>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {page.features.map((f,i)=>(
            <div key={i} style={{display:"flex",gap:12,padding:"14px 18px",background:CR,
              borderRadius:10,border:`1px solid ${B}`}}>
              <span style={{color:page.color,fontWeight:800,flexShrink:0,fontSize:15}}>✓</span>
              <span style={{fontSize:13.5,color:N,lineHeight:1.6}}>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ WHY ═══ */}
      {page.why && (
        <div className="ilp-section-pad" style={{maxWidth:900,margin:"0 auto",padding:"0 20px 48px"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
            <span style={{fontSize:9,fontWeight:800,color:"#dc2626",letterSpacing:2.5,textTransform:"uppercase",
              background:"#fef2f2",padding:"4px 12px",borderRadius:99}}>Why</span>
            <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(20px,4vw,26px)",fontWeight:400,color:N,margin:0}}>
              Why this matters
            </h2>
          </div>
          <div style={{display:"flex",gap:14,alignItems:"flex-start",background:"#fef9f0",
            border:"1px solid #fde68a",borderRadius:14,padding:"20px 18px",flexWrap:"wrap"}}>
            <svg width="40" height="40" viewBox="0 0 40 40" style={{flexShrink:0}}>
              <circle cx="20" cy="20" r="18" fill="#fef3c7" stroke="#d97706" strokeWidth="1.5"/>
              <text x="20" y="27" textAnchor="middle" fontSize="20" fill="#d97706">!</text>
            </svg>
            <div style={{fontSize:14,color:"#78350f",lineHeight:1.8}}>{page.why}</div>
          </div>
        </div>
      )}

      <div className="ilp-section-pad" style={{maxWidth:900,margin:"0 auto",padding:"0 20px 48px"}}>
        <div style={{background:N,borderRadius:16,padding:"24px 22px"}}>
          <div style={{fontSize:9,fontWeight:800,color:GOLD,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>
            Ideal For
          </div>
          <div style={{fontSize:14,color:"rgba(255,255,255,.85)",lineHeight:1.75}}>{page.idealFor}</div>
        </div>
      </div>

      {/* ═══ HOW ═══ */}
      <div className="ilp-section-pad" style={{maxWidth:900,margin:"0 auto",padding:"0 20px 48px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
          <span style={{fontSize:9,fontWeight:800,color:BLUE,letterSpacing:2.5,textTransform:"uppercase",
            background:"#eff6ff",padding:"4px 12px",borderRadius:99}}>How</span>
          <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(20px,4vw,26px)",fontWeight:400,color:N,margin:0}}>
            How it works
          </h2>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
          {page.howItWorks.map((step,i)=>(
            <div key={i} style={{background:W,border:`1px solid ${B}`,borderRadius:12,padding:"20px 18px"}}>
              <div style={{width:30,height:30,borderRadius:9,background:page.color,color:W,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,marginBottom:12}}>
                {i+1}
              </div>
              <div style={{fontSize:13,color:SL,lineHeight:1.65}}>{step}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ RELATED PAGES + RESPONSIBLE USE ═══ */}
      {(() => {
        const isSolution = category === "solution";
        const rel = isSolution ? null : relatedForInstrument(slug);
        const instrumentLinks = (isSolution ? FEATURED_INSTRUMENTS : rel.instruments).filter(k => INSTRUMENT_PAGES[k]);
        const solutionLinks = (isSolution ? SOLUTION_SLUGS.filter(k => k !== slug) : rel.solutions).filter(k => SOLUTION_PAGES[k]);
        const linkStyle = { display:"block", padding:"10px 14px", background:CR, border:`1px solid ${B}`, borderRadius:10, color:N, fontSize:13, fontWeight:600, textDecoration:"none", lineHeight:1.4 };
        return (
          <div className="ilp-section-pad" style={{maxWidth:900,margin:"0 auto",padding:"0 20px 40px"}}>
            <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(20px,4vw,26px)",fontWeight:400,color:N,marginBottom:16}}>
              {isSolution ? "Related solutions and assessments" : `More ${rel.group ? rel.group.toLowerCase() : "assessments"} and where they are used`}
            </h2>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:10,marginBottom:16}}>
              {instrumentLinks.map(k => <a key={k} href={`/instruments/${k}`} style={linkStyle}>{INSTRUMENT_PAGES[k].name}</a>)}
              {solutionLinks.map(k => <a key={k} href={`/solutions/${k}`} style={linkStyle}>{SOLUTION_PAGES[k].name}</a>)}
              <a href="/reports" style={linkStyle}>Sample reports for every assessment</a>
            </div>
            <div style={{background:CR,border:`1px solid ${B}`,borderRadius:10,padding:"14px 18px",fontSize:12.5,color:SL,lineHeight:1.7}}>
              <strong style={{color:N}}>Using results responsibly.</strong> Assessment results describe tendencies and preferences at the time of
              testing; they are one input to a decision, not a verdict. Use them alongside a structured interview, work samples and references, tell
              candidates how their results will be used, and avoid treating any single score as a pass or fail line unless it has been validated for
              that purpose in your own setting.
            </div>
          </div>
        );
      })()}

      <div className="ilp-section-pad" style={{maxWidth:900,margin:"0 auto",padding:"0 20px 48px"}}>
        <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(20px,4vw,26px)",fontWeight:400,color:N,marginBottom:20}}>
          Frequently asked questions
        </h2>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {page.faqs.map((f,i)=>(
            <div key={i} style={{background:CR,border:`1px solid ${B}`,borderRadius:10,padding:"18px 20px"}}>
              <div style={{fontSize:13.5,fontWeight:700,color:N,marginBottom:8}}>{f.q}</div>
              <div style={{fontSize:12.5,color:SL,lineHeight:1.65}}>{f.a}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{background:`linear-gradient(135deg,${N},${N}dd)`,padding:"40px 20px"}}>
        <div style={{maxWidth:700,margin:"0 auto",textAlign:"center"}}>
          <h2 style={{fontFamily:"Fraunces,serif",fontSize:"clamp(22px,5vw,28px)",fontWeight:400,color:W,marginBottom:14}}>
            Ready to try {page.name}?
          </h2>
          <p style={{fontSize:13.5,color:"rgba(255,255,255,.7)",marginBottom:24}}>
            Start with a free trial — no credit card required.
          </p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <button onClick={onEnter} className="ilp-final-cta-btn" style={{background:GOLD,color:N,border:"none",borderRadius:10,
              padding:"13px 28px",fontSize:13.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
              Start Free Trial →
            </button>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="ilp-final-cta-btn" style={{background:"rgba(255,255,255,.1)",
              color:W,border:"1.5px solid rgba(255,255,255,.2)",borderRadius:10,padding:"13px 28px",
              fontSize:13.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textDecoration:"none",
              display:"inline-block"}}>
              💬 WhatsApp Us
            </a>
          </div>
        </div>
      </div>

      <div style={{padding:"20px 16px",textAlign:"center",fontSize:11,color:MU,borderTop:`1px solid ${B}`,wordBreak:"break-word"}}>
        AXIS360 by NonVerbals Consulting · assess.nonverbals.com · nizam@nonverbals.com · {PHONE_D}
      </div>
    </div>
  );
}
